import React, { Component, ErrorInfo, ReactNode } from "react";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  featureName?: string;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * A lightweight error boundary for wrapping individual features/sections.
 * Unlike the global ErrorBoundary, this renders inline and allows retry
 * without a full page reload.
 */
export class FeatureErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(`FeatureErrorBoundary${this.props.featureName ? `: ${this.props.featureName}` : ""} caught an error`, error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Card className="p-6" role="alert">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">
                {this.props.featureName
                  ? `${this.props.featureName} encountered an error`
                  : "Something went wrong"}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                This section failed to load. Try again or refresh the page.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={this.handleRetry}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}
